import produce from 'immer';
import { Action, Reducer } from 'redux';
import { changeLangSurvey } from '../common/helperFunctions';
import { EditObject } from '../common/constants';
import { GroupModel } from '../models/group.model';
import { QuestionModel } from '../models/question.model';
import { SurveyModel } from '../models/survey.model';
import { QuestionTypeModel } from '../models/questionType.model';
import { SearchState } from '../models/SearchState.model';
import { AppThunkAction } from './';
import { MatrixRowColumnModel, McqOptionModel } from '../models/mcq-option.model';
import * as constants from '../common/constants';
import { SurveyEditSteps } from '../common/constants';
import { arrayMove } from 'react-sortable-hoc';
import { SurveyListModel } from '../models/searchSurvey.model';
import authHeader from '../services/auth-header';
import AuthService from '../services/auth.service';

export interface DataState {
    canLoadUIFlag: number, // -1 = not loaded from server, 0 = cannot load, 1 = can load
    isLoading: boolean,
    isSubmitted: boolean,
    errorInSubmit: boolean,
    SubmitMessage: string,
    tag: number,
    step: number,
    language: string,
    surveyId: string,
    survey: SurveyModel,
    surveyCopy: SurveyModel,
    isSurveyDirty: boolean;

    questionType: QuestionTypeModel[],
    surveyStatusList: QuestionTypeModel[],
    surveyStatusPublishedId: number;

    questionIndex: number,
    editQuestion: QuestionModel,
    
    groupIndex: number,
    editGroup: GroupModel,
    searchState: SearchState
}

export const FETCH_SURVEY_LIST = '[CREATE SURVEY] FETCH_SURVEY_LIST';

export const FETCH_SURVEY = '[CREATE SURVEY] FETCH_SURVEY';
export const RECEIVE_SURVEY = '[CREATE SURVEY] RECEIVE_SURVEY';
export const RECEIVE_QUESTION_TYPE = '[CREATE SURVEY] RECEIVE_QUESTION_TYPE';
export const RECEIVE_SURVEY_STATUS_LIST = '[CREATE SURVEY] RECEIVE_SURVEY_STATUS_LIST';
export const SET_PROPERTY = '[CREATE SURVEY] SET_PROPERTY';
export const COPY_QUESTION = '[CREATE SURVEY] COPY_QUESTION';
export const START_EDIT_QUESTION = '[CREATE SURVEY] START_EDIT_QUESTION';
export const END_EDIT_QUESTION = '[CREATE SURVEY] END_EDIT_QUESTION';
export const MOVE_QUESTION = '[CREATE SURVEY] MOVE_QUESTION';
export const DELETE_QUESTION = '[CREATE SURVEY] DELETE_QUESTION';

export const START_EDIT_GROUP = '[CREATE SURVEY] START_EDIT_GROUP';
export const END_EDIT_GROUP = '[CREATE SURVEY] END_EDIT_GROUP';


export const QUESTION_CHANGE_GROUP = '[CREATE SURVEY] QUESTION_CHANGE_GROUP';
export const DELETE_GROUP = '[CREATE SURVEY] DELETE_GROUP';

export const UPDATE_MCQ_OPTION = '[CREATE SURVEY] UPDATE_MCQ_OPTION';

export const UPDATE_MATRIX_OPTION = '[CREATE SURVEY] UPDATE_MATRIX_OPTION';

export const MOVE_NEXT_STEP = '[CREATE SURVEY] MOVE_NEXT_STEP';
export const SET_CAN_LOAD_UI_FLAG = '[CREATE SURVEY] SET_CAN_LOAD_UI_FLAG';
export const SET_SUBMIT_STATUS = '[CREATE SURVEY] SET_SUBMIT_STATUS';
export const SET_LANGUAGE = '[CREATE SURVEY] SET_LANGUAGE';

interface ReceiveSurveyListCreateSurveyAction {
    type: string;
    searchText: string;
    surveyList: SurveyListModel[];
    isLoading: number;
}

interface FetchCreateSurveyAction {
    type: string;
    surveyCode: string;
    survey: SurveyModel;
}

interface ReceiveCreateSurveyAction {
    type: string;
    surveyCode: string;
    survey: SurveyModel;
    isReload: boolean;
}

interface ReceiveQuestionTypeAction {
    type: string;
    questionType: QuestionTypeModel[]
}

interface ReceiveTypeListAction {
    type: string;
    typeList: QuestionTypeModel[]
}

interface SetPropertyCreateSurveyAction {
    type: string;
    objectType: EditObject;
    propertyName: string;
    propertyValueString: string;
    propertyValueBoolean: boolean;
    propertyValueNumber: number;
    propertyValueDate: Date;
}


interface CopyQuestionAction {
    type: string;
    index: number;
}

interface StartQuestionEditAction {
    type: string;
    index: number;
}

interface EndQuestionEditAction {
    type: string;
    cancelEdit: boolean;
}

interface MoveQuestionAction {
    type: string;
    oldIndex: number;
    newIndex: number;
}

interface MoveNextStepCreateSurveyAction {
    type: string;
    step?: number;
}


interface StartGroupEditAction {
    type: string;
    groupId: number | null;
}

interface EndGroupEditAction {
    type: string;
    cancelEdit: boolean;
}

interface QuestionChangeGroupAction {
    type: string;
    index: number;
    groupId: number | null;
}

interface DeleteGroupAction {
    type: string;
    index: number;
}

interface DeleteQuestionAction {
    type: string;
    index: number;
}

interface UpdateMcqOptionAction {
    type: string;
    options: McqOptionModel[];
}

interface UpdateMatrixOptionAction {
    type: string;
    matrixRows: MatrixRowColumnModel[];
    matrixColumns: MatrixRowColumnModel[];
}

interface SetcanLoadUIFlagCreateSurveyAction {
    type: string;
    canLoadUIFlag: number;
}

interface SetSubmitStatusCreateSurveyAction {
    type: string;
    message: string;
    status: boolean;
    surveyId: number;
}

interface SetLanguageCreateSurveyAction {
    type: string;
    language: string;
    moveNext: boolean;
}

type KnownAction =
    FetchCreateSurveyAction
    | ReceiveCreateSurveyAction
    | ReceiveSurveyListCreateSurveyAction
    | ReceiveQuestionTypeAction
    | ReceiveTypeListAction
    | SetPropertyCreateSurveyAction
    | CopyQuestionAction
    | StartQuestionEditAction
    | EndQuestionEditAction
    | MoveQuestionAction
    | MoveNextStepCreateSurveyAction
    | StartGroupEditAction
    | EndGroupEditAction
    | DeleteQuestionAction
    | QuestionChangeGroupAction
    | DeleteGroupAction
    | UpdateMcqOptionAction
    | UpdateMatrixOptionAction
    | SetcanLoadUIFlagCreateSurveyAction
    | SetSubmitStatusCreateSurveyAction
    | SetLanguageCreateSurveyAction;

export const actionCreators = {
    MoveNextStep: (step?: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore && appState.createSurveyStore.surveyId) {
            dispatch({ type: MOVE_NEXT_STEP, step: step });
        }
    },
    SaveSurvey: (): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore && appState.createSurveyStore) {
            const surveyWithUpdatedStatus = produce(appState.createSurveyStore.survey, df => {
                df.statusId = df.newStatusId
            });

            fetch(`Admin/SaveSurvey`, authHeader("POST", JSON.stringify(surveyWithUpdatedStatus), ''))
                .then(response => {
                    if (response.ok) {
                        const data = response.json() as Promise<number>;
                        data.then(v =>
                            dispatch({ type: SET_SUBMIT_STATUS, message: `Saved successfully!!`, status: false, surveyId: v })
                        );
                    } else {
                        dispatch({ type: SET_SUBMIT_STATUS, message: 'Unable to save', status: true });
                    }
                })
                .catch(error => {
                    dispatch({ type: SET_SUBMIT_STATUS, message: 'Unable to save' + (error || ''), status: true });
                });
        }
    },
    FetchCanLoadUIFlag: (): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore && appState.createSurveyStore.canLoadUIFlag === -1) {
            fetch(`account/checkuser`, authHeader("GET", '', ''))
                .then(response => response.json() as Promise<boolean>)
                .then(data => {
                    dispatch({ type: SET_CAN_LOAD_UI_FLAG, canLoadUIFlag: 1 });
                })
                .catch(error => {
                    dispatch({ type: SET_CAN_LOAD_UI_FLAG, canLoadUIFlag: 0 });
                    AuthService.logout();
                });
        }
    },
    RequestSurveyList: (searchText: string, pageSize: number, pageNumber: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore) {
            fetch(`/Admin/SearchSurvey/${pageSize}/${pageNumber}/${searchText}`, authHeader("GET", '', ''))
            .then(response => response.json() as Promise<SurveyListModel[]>
            )
            .then(data => {
                dispatch({ type: FETCH_SURVEY_LIST, searchText, surveyList: data, isLoading: 0 });
            })
            .catch(error => {
                dispatch({ type: FETCH_SURVEY_LIST, searchText, surveyList: [], isLoading: 0 });
            });

            dispatch({ type: FETCH_SURVEY_LIST, searchText, surveyList: [], isLoading: 1 });
        }
    },
    RequestSurvey: (surveyId: string, isNew: boolean, isReload: boolean): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore) {
            fetch(`/Admin/Search/${surveyId}`, authHeader("GET", '', ''))
                .then(response => response.json() as Promise<SurveyModel>)
                .then(data => {
                    if (isNew) {
                        data.surveyId *= -1;
                        data.statusId = -1;
                        
                        for(let i=0; i < data.groups.length; i++) {
                            data.groups[i].groupId *= -1;
                        }

                        for(let i=0; i < data.questions.length; i++) {
                            data.questions[i].groupId *= -1;
                            data.questions[i].questionId *= -1;
                            
                            for(let p=0; p < data.questions[i].mcqOptions.length; p++) {
                                data.questions[i].mcqOptions[p].mcqOptionId *= -1;
                            }
                        }
                    }

                    dispatch({ type: RECEIVE_SURVEY, surveyCode: surveyId, survey: data, isReload: isReload });
                })
                .catch(error => {
                    dispatch({ type: MOVE_NEXT_STEP, step: -1 });
                });

            if (appState.createSurveyStore.questionType.length === 0) {
                fetch(`/Admin/GetQuestionType`, authHeader("GET", '', ''))
                .then(response => response.json() as Promise<QuestionTypeModel[]>)
                .then(data => {
                    dispatch({ type: RECEIVE_QUESTION_TYPE, questionType: data });
                });
            }

            if (appState.createSurveyStore.surveyStatusList.length === 0) {
                fetch(`/Admin/GetSurveyStatusList`, authHeader("GET", '', ''))
                .then(response => response.json() as Promise<QuestionTypeModel[]>)
                .then(data => {
                    dispatch({ type: RECEIVE_SURVEY_STATUS_LIST, typeList: data });
                });
            }
            
            if (isReload === false)
                dispatch({ type: FETCH_SURVEY, surveyCode: surveyId, survey: new SurveyModel() });
        }
    },
    SetPropertyCreateSurvey: (objectType: EditObject, propertyName: string, propertyValueString: string, propertyValueBoolean: boolean, propertyValueNumber: number, propertyValueDate: Date): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: SET_PROPERTY, objectType, propertyName, propertyValueString, propertyValueBoolean, propertyValueNumber, propertyValueDate });
        }

    },
    CopyQuestion: (index: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: COPY_QUESTION, index: index });
        }

    },
    StartQuestionEdit: (index: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: START_EDIT_QUESTION, index: index });
        }

    },
    EndQuestionEdit: (cancelEdit: boolean): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: END_EDIT_QUESTION, cancelEdit });
        }

    },
    MoveQuestion: (oldIndex: number, newIndex: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: MOVE_QUESTION, oldIndex, newIndex });
        }

    },
    DeleteQuestion: (index: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: DELETE_QUESTION, index: index });
        }

    },
    QuestionChangeGroupAction: (index: number, groupId: number | null): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: QUESTION_CHANGE_GROUP, index: index, groupId: groupId });
        }

    },
    DeleteGroup: (index: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: DELETE_GROUP, index: index });
        }

    },
    StartGroupEdit: (index: number): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: START_EDIT_GROUP, groupId: index });
        }

    },
    EndGroupEdit: (cancelEdit: boolean): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: END_EDIT_GROUP, cancelEdit });
        }

    },

    UpdateMcqOption: (options: McqOptionModel[]): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: UPDATE_MCQ_OPTION, options: options });
        }

    },

    UpdateMatrixOption: (matrixRows: MatrixRowColumnModel[], matrixColumns: MatrixRowColumnModel[]): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {

        const appState = getState();
        if (appState && appState.createSurveyStore) {
            dispatch({ type: UPDATE_MATRIX_OPTION, matrixRows, matrixColumns });
        }

    },

    SetLanguage: (language: string, moveNext: boolean ): AppThunkAction<KnownAction> => 
    (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.createSurveyStore && appState.createSurveyStore.surveyId) {
            dispatch({ type: SET_LANGUAGE, language: language, moveNext: moveNext });
        }
    }
};

const unloadedState: DataState = {
    canLoadUIFlag: -1,
    isLoading: false,
    isSubmitted: false,
    errorInSubmit: false,
    SubmitMessage: '',

    surveyId: '',
    step: 0,
    tag: 0,
    language: 'en',
    survey: new SurveyModel(),
    surveyCopy: new SurveyModel(),
    isSurveyDirty: false,

    questionType: [],
    surveyStatusList: [],
    surveyStatusPublishedId: -1,

    questionIndex: -1,
    editQuestion: new QuestionModel(),
    
    groupIndex: -1,
    editGroup: new GroupModel(),
    searchState: new SearchState()
};

export const reducer: Reducer<DataState> = (state: DataState = unloadedState, incomingAction: Action): DataState => {

    if (state === undefined) {
        return unloadedState;
    }

    const tempState = produce(state, draftState => {
        if (incomingAction.type === FETCH_SURVEY) {
            const action = incomingAction as FetchCreateSurveyAction;

            //draftState.canLoadUIFlag = -1;
            draftState.isSubmitted = false;
            draftState.errorInSubmit = false;
            draftState.SubmitMessage = '';
        
            draftState.tag = 0;
            draftState.language = 'en';
            draftState.survey = new SurveyModel();
            draftState.surveyCopy = new SurveyModel();
            
            draftState.questionIndex = -1;
            draftState.editQuestion = new QuestionModel();
            
            draftState.groupIndex = -1;
            draftState.editGroup = new GroupModel();

            draftState.isLoading = true;
            draftState.surveyId = action.surveyCode;
            draftState.step = SurveyEditSteps.Loading;

        } else if (incomingAction.type === FETCH_SURVEY_LIST) {
            const action = incomingAction as ReceiveSurveyListCreateSurveyAction;

            if (action.isLoading != 1) {
                if (action.surveyList && action.surveyList.length > 0)
                    draftState.searchState.totalRecords = action.surveyList[0].totalRecords;
                else 
                    draftState.searchState.totalRecords = 0;


                draftState.searchState.survey = action.surveyList;
            }
            draftState.searchState.isLoading = action.isLoading;
            draftState.tag += 1;

        } else if (incomingAction.type === RECEIVE_SURVEY) {
            const action = incomingAction as ReceiveCreateSurveyAction;

            if (action.surveyCode === draftState.surveyId) {
                //return {...state,
                    draftState.isLoading = false;
                    draftState.isSubmitted = false;
                    draftState.errorInSubmit = false;
                    draftState.SubmitMessage = '';
               
                    draftState.surveyId = action.surveyCode;
                    draftState.survey = changeLangSurvey(action.survey, draftState.language);
                    draftState.survey.newStatusId = draftState.survey.statusId;
                    draftState.surveyCopy = changeLangSurvey(action.survey, draftState.language);
                    draftState.surveyCopy.newStatusId = draftState.survey.statusId;
                    
                    if (action.isReload)
                        draftState.step = SurveyEditSteps.Publish;
                    else
                        draftState.step = SurveyEditSteps.Start;
                // };
            }
        } else if (incomingAction.type === RECEIVE_SURVEY_STATUS_LIST) {
            const action = incomingAction as ReceiveTypeListAction;

            draftState.surveyStatusList = action.typeList;
            const publishStatus = action.typeList.find( e => e.code === constants.SurveryState_Published);

            if (publishStatus) {
                draftState.surveyStatusPublishedId = publishStatus.id;
            }
    

        } else if (incomingAction.type === RECEIVE_QUESTION_TYPE) {
            const action = incomingAction as ReceiveQuestionTypeAction;

            draftState.questionType = action.questionType;
            
        } else if (incomingAction.type === SET_PROPERTY) {
            const action = incomingAction as SetPropertyCreateSurveyAction;

            draftState.tag += 1;
            
            switch (action.propertyName) {
                case 'name':
                case 'titleEn':
                case 'titleFr':
                case 'startNotesEn':
                case 'startNotesFr':
                case 'endNotesEn':
                case 'endNotesFr':
                    draftState.survey[action.propertyName] = action.propertyValueString;
                    break;

                case 'startDate':
                case 'endDate':
                    draftState.survey[action.propertyName] = action.propertyValueDate;
                    break;

                case 'newStatusId':
                    draftState.survey[action.propertyName] = action.propertyValueNumber;
                    break;

                case 'questionEn':
                case 'questionFr':
                case 'placeholderEn':
                case 'placeholderFr':
                case 'errorMessageEn':
                case 'errorMessageFr':
                    draftState.editQuestion[action.propertyName] = action.propertyValueString;
                    break;
                                        
                case 'isMandatory':
                    draftState.editQuestion[action.propertyName] = action.propertyValueBoolean;
                    break;
    
                case 'maxCharacterLimit':
                case 'maxMcqValues':
                case 'minMcqValues':
                    draftState.editQuestion[action.propertyName] = action.propertyValueNumber;
                    break;
    
                case 'isValid':
                    draftState.editQuestion[action.propertyName] = action.propertyValueBoolean;
                    break;

                case 'questionTypeId':
                    draftState.editQuestion[action.propertyName] = action.propertyValueNumber;
                    const typ = state.questionType.find(f => f.id === action.propertyValueNumber);

                    if (typ != null) {
                        draftState.editQuestion.code = typ.code;
                        draftState.editQuestion.defaultRegex = typ.defaultRegex;
                    }

                    break;
    
                case 'groupHeadingEn':
                case 'groupHeadingFr':
                case 'groupSubHeadingEn':
                case 'groupSubHeadingFr':
                    draftState.editGroup[action.propertyName] = action.propertyValueString;
                    break;

                case 'searchText':
                    draftState.searchState[action.propertyName] = action.propertyValueString;
                    break;

                case 'pageNumber':
                case 'totalRecords':
                case 'pageSize':
                    draftState.searchState[action.propertyName] = action.propertyValueNumber;
                    break;

            }
        } else if (incomingAction.type === MOVE_NEXT_STEP) {
            const action = incomingAction as MoveNextStepCreateSurveyAction;
            let stepLevel = draftState.step + 1;

            if (action.step !== undefined)
                stepLevel = action.step;

            draftState.step = stepLevel;
            
        } else if (incomingAction.type === COPY_QUESTION) {
            const action = incomingAction as CopyQuestionAction;

            const questionIndex = action.index;
            draftState.step = SurveyEditSteps.Question;
            // draftState.questionIndex = questionIndex;

            if (action.index !== -1) {
                draftState.editQuestion = produce(state.survey.questions[questionIndex], dr => {

                    dr.questionId = 0;
                    
                    for(let p=0; p < dr.mcqOptions.length; p++) {
                        dr.mcqOptions[p].mcqOptionId = -1;
                    }
                });
            } else {
                draftState.editQuestion = new QuestionModel();
            }

        } else if (incomingAction.type === START_EDIT_QUESTION) {
            const action = incomingAction as StartQuestionEditAction;

            const questionIndex = action.index;
            draftState.step = SurveyEditSteps.Question;
            draftState.questionIndex = questionIndex;

            if (action.index !== -1)
                draftState.editQuestion = state.survey.questions[questionIndex];
            else
                draftState.editQuestion = new QuestionModel();

        } else if (incomingAction.type === END_EDIT_QUESTION) {
            const action = incomingAction as EndQuestionEditAction;

            if (action.cancelEdit === false) {
                if (state.questionIndex >= 0) {
                    draftState.survey.questions[state.questionIndex] = state.editQuestion;
                } else {
                    draftState.survey.questions.push(state.editQuestion);
                }
            }
            
            draftState.editQuestion = new QuestionModel();
            draftState.questionIndex = -1;

        } else if (incomingAction.type === DELETE_QUESTION) {
            const action = incomingAction as DeleteQuestionAction;

            if (action.index >= 0 && action.index < state.survey.questions.length) {

                draftState.survey.questions.splice(action.index, 1);
    
            }
            
            draftState.editQuestion = new QuestionModel();
            draftState.questionIndex = -1;
            
        } else if (incomingAction.type === MOVE_QUESTION) {
            const action = incomingAction as MoveQuestionAction;

            draftState.survey.questions = arrayMove(draftState.survey.questions, action.oldIndex, action.newIndex);
            
        } else if (incomingAction.type === START_EDIT_GROUP) {
            const action = incomingAction as StartGroupEditAction;

            const groupIndex = state.survey.groups.findIndex(f => f.groupId === action.groupId);
            
            //draftState.step = SurveyEditSteps.Group;
            draftState.groupIndex = groupIndex;

            if (groupIndex >= 0) {
                draftState.editGroup = state.survey.groups[groupIndex];
            } else {
                draftState.editGroup = new GroupModel();
            }

        } else if (incomingAction.type === END_EDIT_GROUP) {
            const action = incomingAction as EndGroupEditAction;

            if (action.cancelEdit === false) {
                if (state.groupIndex >= 0) {
                    draftState.survey.groups[state.groupIndex] = state.editGroup;
                } else {
                    let groupId = (state.survey.groups.length + 1) * -1;

                    while(state.survey.groups.findIndex(f => f.groupId === groupId) > -1 ) {
                        groupId--;
                    }
                    
                    draftState.survey.groups.push(state.editGroup);

                    draftState.survey.groups[state.survey.groups.length].groupId = groupId;
                }
            }
            
            draftState.editGroup = new GroupModel();
            draftState.groupIndex = -1;
        } else if (incomingAction.type === DELETE_GROUP) {
            const action = incomingAction as DeleteGroupAction;

            if (action.index >= 0 && action.index < state.survey.groups.length) {

                const grp = state.survey.groups[action.index];

                for(let x = 0; x < state.survey.questions.length; x++) {
                
                    if (state.survey.questions[x].groupId === grp.groupId) {
                        draftState.survey.questions[x].groupId = -999;
                    }
                }

                draftState.survey.groups.splice(action.index, 1);
            }
            
            draftState.editGroup = new GroupModel();
            draftState.questionIndex = -1;
            
        } else if (incomingAction.type === QUESTION_CHANGE_GROUP) {
            const action = incomingAction as QuestionChangeGroupAction;

            if (action.index >= 0 && action.index < state.survey.questions.length) {

                const grp = state.survey.groups.find(f => f.groupId === action.groupId);
                
                // if (grp) {
                    draftState.survey.questions[action.index].groupId = grp ? grp.groupId : 0;
                // }
            }
        } else if (incomingAction.type === UPDATE_MCQ_OPTION) {
            const action = incomingAction as UpdateMcqOptionAction;

            draftState.editQuestion.mcqOptions = action.options;
        
        } else if (incomingAction.type === UPDATE_MATRIX_OPTION) {
            const action = incomingAction as UpdateMatrixOptionAction;

            draftState.editQuestion.matrixColumns = action.matrixColumns;
            draftState.editQuestion.matrixRows = action.matrixRows;
            
        } else if (incomingAction.type === SET_CAN_LOAD_UI_FLAG) {
            const action = incomingAction as SetcanLoadUIFlagCreateSurveyAction;

            draftState.canLoadUIFlag = action.canLoadUIFlag;

        } else if (incomingAction.type === SET_SUBMIT_STATUS) {
            const action = incomingAction as SetSubmitStatusCreateSurveyAction;

            draftState.SubmitMessage = action.message;
            draftState.errorInSubmit = action.status;
            draftState.isSubmitted = true;

            if (action.status === false) {
                draftState.surveyId = ""+action.surveyId;
                draftState.survey.surveyId = action.surveyId;
            }

        } else if (incomingAction.type === SET_LANGUAGE) {
            const action = incomingAction as SetLanguageCreateSurveyAction;

            const step = action.moveNext ? draftState.step + 1 :draftState.step;
            
            draftState.language = action.language;
            draftState.step = step;
            draftState.survey = changeLangSurvey(draftState.survey, action.language);
            draftState.surveyCopy = changeLangSurvey(draftState.surveyCopy, action.language);
        }
    });

    const isDirty = JSON.stringify(tempState.survey) !== JSON.stringify(tempState.surveyCopy);

    return produce(tempState, draftState => {
        draftState.isSurveyDirty = isDirty;
    });
};
